export type RoleDisplayNameKey = "admin" | "manager" | "user";
type CustomRoleConfig = { name?: string; allowedTabs?: string[] } | null;

export type RoleDisplayNames = Partial<Record<RoleDisplayNameKey, string>>;

const DEFAULT_ROLE_LABELS: Record<RoleDisplayNameKey, string> = {
  admin: "Admin",
  manager: "Manager",
  user: "User",
};

const DEFAULT_ROLE_PLURAL_LABELS: Record<RoleDisplayNameKey, string> = {
  admin: "Admins",
  manager: "Managers",
  user: "Users",
};

const pluralize = (label: string) => {
  if (!label) return label;
  if (label.endsWith("s")) return label;
  if (label.endsWith("y")) return `${label.slice(0, -1)}ies`;
  return `${label}s`;
};

export const getRoleDisplayNamesFromStorage = (): RoleDisplayNames => {
  if (typeof window === "undefined") return {};

  try {
    const org = localStorage.getItem("org");
    return org ? JSON.parse(org)?.roleDisplayNames || {} : {};
  } catch {
    return {};
  }
};

const getCustomRoleFromStorage = (): CustomRoleConfig => {
  if (typeof window === "undefined") return null;

  try {
    const org = localStorage.getItem("org");
    return org ? JSON.parse(org)?.customRole || null : null;
  } catch {
    return null;
  }
};

export const getRoleDisplayName = (
  role: string,
  options: {
    plural?: boolean;
    roleDisplayNames?: RoleDisplayNames;
    customRole?: CustomRoleConfig;
  } = {}
) => {
  const normalizedRole = role?.toLowerCase();

  if (normalizedRole === "superadmin") {
    return options.plural ? "Super Admins" : "Super Admin";
  }

  if (normalizedRole === "custom") {
    const customLabel =
      options.customRole?.name?.trim() ||
      getCustomRoleFromStorage()?.name?.trim();
    if (customLabel) {
      return options.plural ? pluralize(customLabel) : customLabel;
    }
    return options.plural ? "Customs" : "Custom";
  }

  if (!["admin", "manager", "user"].includes(normalizedRole)) {
    return role;
  }

  const key = normalizedRole as RoleDisplayNameKey;
  const roleDisplayNames =
    options.roleDisplayNames || getRoleDisplayNamesFromStorage();
  const customLabel = roleDisplayNames?.[key]?.trim();

  if (customLabel) {
    return options.plural ? pluralize(customLabel) : customLabel;
  }

  return options.plural
    ? DEFAULT_ROLE_PLURAL_LABELS[key]
    : DEFAULT_ROLE_LABELS[key];
};
